import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import withErrorContainer from '@zr/components/withErrorContainer';
import fetchNextAction from '../api/fetchNextAction';

import { MODAL } from './flowLayouts';
import { applyParametersPropTypes, applyParametersProps } from './applyFlowPropTypes';
import { getScreenTypeComponent } from './screenTypeMap';
import ApplyingToHeader from './ApplyingToHeader';
import { CloseButton } from './CloseButton';

import ScreenModal from './Layouts/ScreenModal';
import WarningModal from './Layouts/WarningModal';
import ExistingEmailAddressModal from './Layouts/ExistingEmailAddressModal';

import { ELIGIBILITY_CELEBRATION, FINALIZE_APPLY, COLLECT_EMAIL_ADDRESS, MOBILE_APP_NOTIFICATION_PROMO, MOBILE_APP_PRODUCT_PROMO } from './screenTypeSchema';

import './ApplyFlowApp.less';

// Combine server data and apply data
function buildApplyData({
  applyParameters,
  initializerData,
}) {
  // This avoids a deep merge to keep all the AB tests
  const abTests = { ...initializerData?.ab_tests, ...applyParameters?.ab_tests };

  const applyData = {
    ...initializerData,
    ...applyParametersProps, // explicitly set defaults for logging purposes
    ...applyParameters,
  };

  applyData.ab_tests = abTests;

  return applyData;
}

const API_ACTIONS = [FINALIZE_APPLY];
const HIDE_APPLY_HEADER_STEPS = [ELIGIBILITY_CELEBRATION, MOBILE_APP_NOTIFICATION_PROMO, MOBILE_APP_PRODUCT_PROMO];

/**
 * Callback for handling imperative DOM updates after closing the Apply Flow, such as displaying a toaster or changing
 * button states.
 * @example
 * // handle when a user closes the Apply Flow exits and the application has not been started
 * onDone('not_started')
 * @example
 * // handle when a user closes the Apply Flow and an application is partially complete
 * onDone('in_progress')
 * @example
 * // handle when a user successfully completes an Apply Flow application
 * onDone('delivered')
 *
 * @callback onDoneCallback
 * @param {('not_started'|'in_progress'|'delivered')} applicationState - calls onDone with the state of the application
 * @param {string} encryptedContactId - Encrypted Contact ID
 */

/**
 * Component for displaying the Apply Flow App
 *
 * areaName and method are used by log_event and can optionally be generated by the eventNameBuilder function found in
 * web/common/frontend/js/generate_event_names.ts or app/view/static/js/common/_generate_event_names.js
 *
 * @component
 * @example
 * <ApplyFlowApp applyParameters={
 *   encryptedQuizId: 'a123456',
 *   listingVersionKey: 'a123456',
 *   placementId: 'a123456',
 *   areaName: 'job-results.apply',
 *   method: 'fe.job-results.one_click_apply',
 * }/>
 *
 * @example
 * <ApplyFlowApp applyParameters={
 *   encryptedQuizId: 'a123456',
 *   listingVersionKey: 'a123456',
 *   placementId: 'a123456',
 *   ...eventNameBuilder({template: 'job-results', description: 'one_click_apply'})
 * }/>
 *
 * @param {Object} applyParameters - The data required to launch the Apply Flow.
 * @param {Object} [applyParameters.ab_tests={}] - Key/value pairs of AB Tests similar to PageView event properties.
 * @param {string} applyParameters.areaName - used by log_event to identify the originating area name for user interactions.
 * @param {string} applyParameters.method - used by log_event to identify the originating method for user interactions.
 * @param {string} applyParameters.encryptedQuizId - Encrypted Quiz ID.
 * @param {onDoneCallback} [applyParameters.onDone] - Callback to run when Apply Flow completes.
 * @param {boolean} [applyParameters.redirectAfterApply=false] - Redirect to action_url from API after finalize apply step.
 * @param {string} applyParameters.listingVersionKey - Listing Version Key provided by mixer.
 * @param {number} applyParameters.placementId - Placement ID provided by mixer.
 *
 * @public
 */
export function ApplyFlowApp({
  applyParameters,
  onError,
}) {
  const [applyData, setApplyData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [screenTypeObject, setScreenTypeObject] = useState(null);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openExistingEmailModal, setOpenExistingEmailModal] = useState(false);

  const { encryptedQuizId, listingVersionKey } = applyParameters;
  const encryptedResponseId = applyData?.encrypted_response_id;
  const isMobileAppPromo = screenTypeObject?.named_action === 'ii_notification_promo'
   || screenTypeObject?.named_action === 'ii_product_promo';

  // prop-types library cannot set defaultProps on nested objects, consider re-working
  // https://github.com/facebook/react/issues/2568
  const layout = applyParameters.layout || MODAL;

  const handleCloseModal = useCallback(() => {
    // This is the final close step that unsets all of our states
    setModalOpen(false);
    setApplyData(null);
    setScreenTypeObject(null);
    setIsLoading(false);
  }, [setApplyData, setIsLoading, setModalOpen, setScreenTypeObject]);

  const toggleWarningModal = useCallback(() => {
    // toggle the Apply Flow Modal to open/close
    setModalOpen((prev) => !prev);

    // toggle the Warning modal to open/close
    setWarningModalOpen((prev) => !prev);
  }, [setModalOpen, setWarningModalOpen]);

  const toggleExistingEmailModal = useCallback(() => {
    // toggle the Apply Flow Modal to open/close
    setModalOpen((prev) => !prev);

    // toggle the existing email address modal to open/close
    setOpenExistingEmailModal((prev) => !prev);
  }, [setModalOpen, setOpenExistingEmailModal]);

  const handleWarningClosePrompt = useCallback(() => {
    // Close the Warning modal
    setWarningModalOpen(false);

    // Calling onDone with the current application state will allow the jQuery code which launched this apply flow
    // to update it's button UI appropriately. e.g. quick_apply.js will be able to change button text to "Answer Questions"
    // if onDone is called with `in_progress'.
    applyData?.onDone(screenTypeObject.application_state, screenTypeObject.enc_contact_id);

    // Let's close the modal
    handleCloseModal();
  }, [applyData, screenTypeObject, handleCloseModal, setWarningModalOpen]);

  // extraSearchParams allows individual components to send custom params to the next-action endpoint. These are
  // crucial to the function of the Apply Flow: e.g. include_flow_params=1 or completed_merge_parsed_resume=1
  // TODO: Document this better in future
  const getNextAction = (extraSearchParams = {}) => fetchNextAction({
    encryptedResponseId,
    encryptedQuizId,
    extraSearchParams,
    listingVersionKey,
  });

  const handleNextAction = async (extraSearchParams) => {
    // ApplyFlowApp controls the various screenTypes. When one of its screenTypes (i.e. CollectEmailAddress) disables
    // the close button during its processing/loading phase, we need to `setIsLoading(false)` here so that the
    // close button will be available in the next screen.
    // We set the next screen by calling `setScreenTypeObject(nextAction)`
    setIsLoading(false);

    const nextAction = await getNextAction(extraSearchParams);

    setScreenTypeObject(nextAction);
  };

  // Initializer
  useEffect(() => {
    let ignore = false;

    async function initializeApplyFlow() {
      const firstAction = await fetchNextAction({
        encryptedQuizId,
        extraSearchParams: { include_flow_params: 1 }, // Ask the API for the first action
        listingVersionKey,
      });

      if (!ignore) {
        const initialApplyData = buildApplyData({
          applyParameters,
          initializerData: firstAction.data.flow_parameters,
        });

        setApplyData(initialApplyData);
        setScreenTypeObject(firstAction);
        setModalOpen(true);
      }
    }

    if (encryptedQuizId) {
      initializeApplyFlow();
    }
    return () => {
      ignore = true;
    };
  }, [
    applyParameters,
    encryptedQuizId,
    listingVersionKey,
  ]);

  if (modalOpen) {
    const namedAction = screenTypeObject.named_action;
    const applicationState = screenTypeObject.application_state;
    const ScreenTypeComponent = getScreenTypeComponent(namedAction);

    const Component = (
      <ScreenTypeComponent
        applyData={applyData}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        onError={onError}
        onScreenTypeFinish={namedAction === FINALIZE_APPLY ? handleCloseModal : handleNextAction}
        setApplyData={setApplyData}
        screenType={namedAction}
        screenTypeObject={screenTypeObject}
        screenTypeParameters={screenTypeObject.data}
        toggleExistingEmailModal={namedAction === COLLECT_EMAIL_ADDRESS ? toggleExistingEmailModal : null}
      />
    );

    // These components don't render a UI
    if (API_ACTIONS.includes(namedAction)) {
      return Component;
    }

    const showHeader = !HIDE_APPLY_HEADER_STEPS.includes(namedAction);

    return (
      <ScreenModal
        isOpen={modalOpen}
        screenType={namedAction}
      >
        <div className={`ApplyFlowApp ${layout} ${namedAction}`}>
          <div className={`header_wrapper ${showHeader ? 'show_header' : ''} ${isMobileAppPromo ? 'mobile_app_promo_header' : ''}`}>
            {showHeader && (
              <ApplyingToHeader
                companyLogo={applyData.company_logo}
                companyName={applyData.company_name}
                jobLocation={applyData.job_location}
                jobTitle={applyData.job_title}
                layout={layout}
              />
            )}
            <CloseButton
              applicationState={applicationState}
              applyData={applyData}
              handleCloseModal={handleCloseModal}
              isLoading={isLoading}
              screenType={namedAction}
              toggleWarningModal={toggleWarningModal}
            />
          </div>
          <section className={`apply_flow_screen ${isMobileAppPromo ? 'mobile_app_promo' : ''}`}>
            {Component}
          </section>
        </div>
      </ScreenModal>
    );
  }

  if (warningModalOpen) {
    return (
      <WarningModal
        applyData={applyData}
        isMobile={applyData.is_mobile}
        handleWarningGoBackPrompt={toggleWarningModal}
        handleWarningClosePrompt={handleWarningClosePrompt}
        applicationState={screenTypeObject.application_state}
      />
    );
  }

  if (openExistingEmailModal) {
    return (
      <ExistingEmailAddressModal
        applyData={applyData}
        setOpenExistingEmailModal={setOpenExistingEmailModal}
        onCloseModal={handleCloseModal}
        screenTypeObject={screenTypeObject}
      />
    );
  }

  return null;
}

ApplyFlowApp.propTypes = {
  applyParameters: applyParametersPropTypes,
  onError: PropTypes.func,
};

ApplyFlowApp.defaultProps = {
  applyParameters: applyParametersProps,
  onError: () => {},
};

export default withErrorContainer(ApplyFlowApp);
